import React, { useMemo } from 'react'
import Select from 'react-select'

// Searchable predicate ("operator") picker. Type to filter — react-select's
// default filter matches both the human label and the technical name (value),
// so users don't have to know the exact predicate name. Options stay grouped
// by category. The menu portals to <body> so it isn't clipped inside the
// recipe-builder modal.
const PredicatePicker = ({ value, onChange, registry, disabled }) => {
  const grouped = useMemo(() => {
    if (!registry?.predicates) return []
    const byCategory = {}
    Object.entries(registry.predicates).forEach(([name, schema]) => {
      const cat = schema.category || 'Other'
      ;(byCategory[cat] ||= []).push({ value: name, label: schema.label || name })
    })
    const categories = registry.categories || Object.keys(byCategory)
    return categories
      .filter((cat) => byCategory[cat])
      .map((cat) => ({
        label: cat,
        options: byCategory[cat].sort((a, b) => a.label.localeCompare(b.label))
      }))
  }, [registry])

  const selected = useMemo(
    () => grouped.flatMap((g) => g.options).find((o) => o.value === value) || null,
    [grouped, value]
  )

  return (
    <Select
      classNamePrefix="predicate-select"
      options={grouped}
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : '')}
      isDisabled={disabled}
      isSearchable
      placeholder="Search predicates…"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      styles={{
        // Fill the available row width (the old <select> was block/100%) so the
        // control width is stable instead of shrinking to the selected label.
        container: (base) => ({ ...base, flexGrow: 1, minWidth: 180 }),
        menuPortal: (base) => ({ ...base, zIndex: 1100 }),
        control: (base) => ({ ...base, minHeight: 31, fontSize: '0.875rem' }),
        dropdownIndicator: (base) => ({ ...base, padding: 4 }),
        clearIndicator: (base) => ({ ...base, padding: 4 }),
        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        groupHeading: (base) => ({ ...base, fontSize: '0.7rem' }),
        option: (base) => ({ ...base, fontSize: '0.875rem' })
      }}
    />
  )
}

export default PredicatePicker
